// @flow
import { Record, Map } from 'immutable';
import Observation from './Observation';
import AsyncValues, { type AsyncValueType } from 'lib/async-value';
// import PublicationStance from './PublicationStance';

type ObservationMap = Map<string, Observation>;
type ObservationContextMap = Map<Id, ObservationMap>;
type ModerationStateShape = {
  ugcId: Id,
  savedObservations: ObservationContextMap,
  currentObservations: ObservationContextMap,
  provisionedObservationTypeCodes: Set<string>,
  observationTypes: ObservationMap,
  savedPublished: null | boolean,
  currentPublished: null | AsyncValueType<boolean>,
};

const defaultModerationStateShape: ModerationStateShape = {
  ugcId: 0,
  provisionedObservationTypeCodes: new Set(),
  savedObservations: new Map(),
  currentObservations: new Map(),
  observationTypes: new Map(),
  savedPublished: null,
  currentPublished: null,
};
const EMPTY_OBSERVATION_DATA = {};
const EMPTY_MAP = new Map();
const PowerReviewsClientId = 1;
const PowerReviewsModerationContext = PowerReviewsClientId;

export default class ModerationState extends Record(
  defaultModerationStateShape
) {
  static create(observationTypes: ObservationMap, payload: ContentBlob) {
    const {
      existing_observations = [],
      pwr_observations = [],
      custom_observations = [],
      ugc: { published, ugc_id },
    } = payload;

    const savedObservations = new Map().withMutations(mp => {
      existing_observations.forEach(item => {
        const { observation_type_code, client_id } = item;
        const observationType = observationTypes.get(observation_type_code);

        if (observationType && client_id) {
          const ob = Observation.createWithData(
            { ...item, name: observationType.name },
            true
          );
          mp.has(client_id)
            ? mp.setIn([client_id, observation_type_code], ob)
            : mp.set(client_id, new Map({ [observation_type_code]: ob }));
        }
      });
    });

    const provisionedObservationTypeCodes = new Set([
      ...pwr_observations,
      ...custom_observations,
    ]);

    const currentObservations = savedObservations;

    return new ModerationState({
      savedObservations,
      currentObservations,
      provisionedObservationTypeCodes,
      observationTypes,
      ugcId: ugc_id,
      savedPublished: published,
      currentPublished: published,
    });
  }

  static setObservation(
    state: ModerationState,
    context: Id,
    observation_type_code: string,
    observation_data?: $PropertyType<
      ObservationBlob,
      'observation_data'
    > = EMPTY_OBSERVATION_DATA
  ): ModerationState {
    const observationType = state.observationTypes.get(observation_type_code);
    if (!state.currentObservations.has(context)) {
      state.setIn(['currentObservations', context], EMPTY_MAP);
    }

    if (observationType) {
      // we should try restoring saved observations
      const savedObservations = ModerationState.getSavedObservations(
        state,
        context
      );
      const observationToSet = savedObservations.has(observation_type_code)
        ? savedObservations.get(observation_type_code)
        : Observation.createWithData(
            { ...observationType, observation_data },
            true
          );
      const currentObservationsHasContext = state.currentObservations.has(
        context
      );
      return currentObservationsHasContext
        ? state.setIn(
            ['currentObservations', context, observation_type_code],
            observationToSet
          )
        : state.setIn(
            ['currentObservations', context],
            new Map({
              [observation_type_code]: observationToSet,
            })
          );
    }

    return state;
  }

  static removeObservation(
    state: ModerationState,
    context: Id,
    observation_type_code: string
  ): ModerationState {
    return state.deleteIn([
      'currentObservations',
      context,
      observation_type_code,
    ]);
  }

  static toggleObservation(
    state: ModerationState,
    context: Id,
    observation_type_code: string
  ): ModerationState {
    const action = state.hasIn([
      'currentObservations',
      context,
      observation_type_code,
    ])
      ? ModerationState.removeObservation
      : ModerationState.setObservation;
    return action(state, context, observation_type_code);
  }

  static applyPowerModeration(state: ModerationState): ModerationState {
    return state.withMutations(st => {
      ModerationState.setObservation(
        st,
        PowerReviewsModerationContext,
        Observation.PowerModerationCode
      );
      // moderation service breaks from convention and throws an error if SO
      // is included
      ModerationState.removeObservation(
        st,
        PowerReviewsModerationContext,
        Observation.SecondOpinionCode
      );
    });
  }

  static applyClientModeration(
    state: ModerationState,
    context: Id
  ): ModerationState {
    return ModerationState.setObservation(
      state,
      context,
      Observation.ClientModerationCode
    );
  }

  static applySecondOpinion(
    state: ModerationState,
    comments: string
  ): ModerationState {
    return ModerationState.setObservation(
      state,
      PowerReviewsModerationContext,
      Observation.SecondOpinionCode,
      { comments }
    );
  }

  static createRequestPayload(
    state: ModerationState,
    context: Id
  ): Array<ObservationBlob> {
    const mapToPayloadItem = ob => ({
      observation_type_code: ob.observation_type_code,
      observation_data: ob.observation_data || {},
      client_id: context,
    });
    const items = state.getIn(['currentObservations', context]);

    return items ? items.map(mapToPayloadItem).toArray() : [];
  }

  static commitObservationBlobs(
    state: ModerationState,
    context: Id,
    payload: Array<ObservationBlob>
  ) {
    const entries = payload.reduce((acc, item) => {
      const { observation_type_code, client_id } = item;

      if (client_id !== context) {
        return acc;
      }

      const observationType = state.observationTypes.get(observation_type_code);
      if (observationType) {
        const ob = Observation.createWithData(
          { ...item, name: observationType.name },
          true
        );

        acc.push([observation_type_code, ob]);
      }

      return acc;
    }, []);

    const savedObservationsForContext = new Map(entries);

    return state.withMutations(it => {
      it.set('savedPublished', it.currentPublished);
      it.setIn(['savedObservations', context], savedObservationsForContext);
      it.setIn(['currentObservations', context], savedObservationsForContext);
    });
  }

  static isPowerReviewsModerated(state: ModerationState): boolean {
    return state.hasIn([
      'savedObservations',
      PowerReviewsModerationContext,
      Observation.PowerModerationCode,
    ]);
  }

  static isClientModerated(state: ModerationState, context: Id): boolean {
    return state.hasIn([
      'savedObservations',
      context,
      Observation.ClientModerationCode,
    ]);
  }

  static hasSecondOpinion(state: ModerationState): boolean {
    return state.hasIn([
      'savedObservations',
      PowerReviewsModerationContext,
      Observation.SecondOpinionCode,
    ]);
  }

  static needsSecondOpinion(state: ModerationState): boolean {
    const isPowerReviewsModerated = ModerationState.isPowerReviewsModerated(
      state
    );
    const hasSecondOpinion = ModerationState.hasSecondOpinion(state);

    return !isPowerReviewsModerated && hasSecondOpinion;
  }

  static getActionableObservations(
    state: ModerationState,
    context: Id
  ): Array<Observation> {
    const provisioned = state.provisionedObservationTypeCodes;
    const currentObservations = ModerationState.getCurrentObservations(
      state,
      context
    );

    return state.observationTypes.reduce((acc, observationType) => {
      const code = observationType.observation_type_code;
      const observation = currentObservations.has(code)
        ? currentObservations.get(code)
        : observationType;

      // we should not show SSC in PWR moderation under any condition
      if (
        code === Observation.ShowServiceComment &&
        context === PowerReviewsModerationContext
      ) {
        return acc;
      }

      let shouldAdd = true;
      shouldAdd = shouldAdd && provisioned.has(code);
      shouldAdd = shouldAdd && Observation.IsEditableObservationTypeCode(code);

      if (shouldAdd) {
        acc.push(observation);
      }

      return acc;
    }, []);
  }

  static getSavedActionableObservations(
    state: ModerationState,
    context: Id
  ): Array<Observation> {
    const provisioned = state.provisionedObservationTypeCodes;
    const savedObservations = ModerationState.getSavedObservations(
      state,
      context
    );

    return state.observationTypes
      .valueSeq()
      .map(
        observation =>
          savedObservations.has(observation.observation_type_code)
            ? savedObservations.get(observation.observation_type_code)
            : observation
      )
      .filter(({ observation_type_code }) =>
        provisioned.has(observation_type_code)
      )
      .filter(Observation.IsEditableObservation)
      .toArray();
  }

  static getSavedObservations(
    state: ModerationState,
    context: Id
  ): ObservationMap {
    return state.savedObservations.get(context, EMPTY_MAP);
  }

  static getCurrentObservations(
    state: ModerationState,
    context: Id
  ): ObservationMap {
    return state.currentObservations.get(context, EMPTY_MAP);
  }

  static isEqual(state: ModerationState, context: Id) {
    // moderation status has changed when existing != current
    const saved = ModerationState.getSavedObservations(state, context);
    const current = ModerationState.getCurrentObservations(state, context);

    if (saved.size !== current.size) {
      return false;
    }
    // $FlowFixMe
    return saved.equals(current);
  }

  static isModified(state: ModerationState, context: Id) {
    return ModerationState.isEqual(state, context) === false;
  }

  static clearPublicationOverrides(
    state: ModerationState,
    context: Id
  ): ModerationState {
    return state.withMutations(st => {
      ModerationState.removeObservation(
        st,
        context,
        Observation.PublicationOverrideCode
      );
      ModerationState.removeObservation(
        st,
        context,
        Observation.NotForPublicationOverrideCode
      );
    });
  }

  static isSavedPublished(state: ModerationState, context: Id): boolean {
    return state.savedPublished;
  }

  static isCurrentPublished(state: ModerationState, context: Id): boolean {
    return state.currentPublished;
  }

  static setCurrentPublished(
    state: ModerationState,
    v: typeof AsyncValues.Pending | boolean
  ): ModerationState {
    return state.set('currentPublished', v);
  }

  static PowerReviewsModerationContext = PowerReviewsModerationContext;

  getId() {
    return this.getUGCId();
  }

  getUGCId() {
    return this.get('ugcId');
  }
}



// WEBPACK FOOTER //
// ./src/models/ModerationState.js